import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IAppState } from '../app/app-state';
import {
    getCompany,
    getCompanySkills,
    getCountry,
    getPermissions,
    getSessionLocale,
    getSkillSets,
    getUser,
    getUserDatetimeFormats
} from './session-selectors';
import { getCompanyPreference, getLocationPreference, getPreference, getPreviousLocationPreference, getUserPreference } from '../preference/preference-selectors';
import { SetLocationPreferenceAction, SetPreferenceAction } from '../preference/preference-actions';
import { Observable, combineLatest, map } from 'rxjs';
import { MomentFormat } from '@core/models/common/moment-format';
import { UserPreference } from '@core/models/preference/user-preference';
import { CompanyPreference } from '@core/models/preference/company-preference';
import { Location } from '@core/models/location/location';
import { UserUpdateRequest } from '@core/data/api/user/user-update-request';
import { UpdateUserAction, UpdateUserEmailAction, UpdateUserPhoneAction } from './session-actions';
import { IObservableAction } from '../shared/actions/observable-action';
import { getBilling } from '../billing/billing-selectors';
import { Billing } from '@core/models/billing/billing';
import { ConnectionPermission } from '@core/models/connection/connection-permission';
import { User } from '@core/models/user/user';
import { Company } from '@core/models/common/company';

@Injectable({ providedIn: 'root' })
export class SessionService {
    constructor( private store$: Store<IAppState>) {}

    getUser() {
        return this.store$.select(getUser);
    }

    getCompany() {
        return this.store$.select(getCompany);
    }
    
    getCompanySkills(): Observable<any> {
        return this.store$.select(getCompanySkills);
    }

    getLocationPreference() {
        return this.store$.select(getLocationPreference);
    }

    getPreviousLocationPreference() {
        return this.store$.select(getPreviousLocationPreference);
    }

    setLocationPreference(userId: string, location: Location, skipExternalUpdate?: boolean) {
        this.store$.dispatch(new SetLocationPreferenceAction({
            userId, location, skipExternalUpdate
        }));
    }

    getCountry() {
        return this.store$.select(getCountry);
    }

    getPreference() {
        return this.store$.select(getPreference);
    }

    setPreference(newPrefs) {
        this.store$.dispatch(new SetPreferenceAction(newPrefs));
    }

    getUserPreference(): Observable<UserPreference> {
        return this.store$.select(getUserPreference);
    }

    getCompanyPreference(): Observable<CompanyPreference> {
        return this.store$.select(getCompanyPreference);
    }

    getUserDatetimeFormats(): Observable<{
        id: number;
        label: string;
        formats: MomentFormat;
    }> {
        return this.store$.select(getUserDatetimeFormats);
    }

    getSessionLocale() {
        return this.store$.select(getSessionLocale);
    }

    getPermissions() {
        return this.store$.select(getPermissions);
    }

    getSkillSets(): Observable<any> {
        return this.store$.select(getSkillSets);
    }

    getSkill(skillId: number): Observable<any> {
        return this.store$.pipe(
            select(getSkillSets),
            map((skills) => {
                const skill =
                    (skills &&
                        skills.filter((s) => s.skill_set_id === skillId)) ||
                    [];
                return skill.length ? skill[0] : null;
            })
        );
    }

    updateUser(userId: string, request: UserUpdateRequest): IObservableAction {
        const action: any = new UpdateUserAction({ id: userId, request });
        this.store$.dispatch(action);
        return action;
    }

    updateUserPhone(userId: string, phone: string, pin: string): IObservableAction {
        const action: any = new UpdateUserPhoneAction({
            id: userId, 
            phone, 
            pin
        });
        this.store$.dispatch(action);
        return action;
    }

    updateUserEmail(userId: string, email: string, pin: string): IObservableAction {
        const action: any = new UpdateUserEmailAction({
            id: userId, 
            email, 
            pin
        });
        this.store$.dispatch(action);
        return action;
    }

    isSessionReady() {
        return combineLatest([
            this.store$.select(getCompany),
            this.store$.select(getUser),
            this.store$.select(getBilling),
            this.store$.select(getPermissions)
        ]).pipe(
            map(([
                company,
                user,
                billing,
                permissions
            ]: [
                Company, User, Billing, ConnectionPermission[]
            ]) => !!company && !!user && !!billing && !!permissions)
        );
    }
}
